(() => {
  const root = document.querySelector('[data-search]');
  if (!root) return;

  const input = root.querySelector('[data-search-input]');
  const panel = root.querySelector('[data-search-panel]');
  // The before-you-type block: trending products and shop chips. Shown while
  // the field holds fewer than two characters, hidden once results take over.
  const idle = root.querySelector('[data-search-idle]');
  const showIdle = (on) => { if (idle) idle.hidden = !on; };
  const results = root.querySelector('[data-search-results]');
  const clearButton = root.querySelector('[data-search-clear]');
  const scrim = document.querySelector('.ssearch__scrim');
  const openers = document.querySelectorAll('[data-search-open]');

  // Shorthands a shopper types instead of a brand's full name. The suggestion
  // endpoint reads titles and vendors but not tags, so "jpg" and "anr" returned
  // nothing at all while both existed as tags on the products.
  const aliases = new Map(
    (root.dataset.searchAliases || '')
      .split('\n')
      .map((line) => line.split('::').map((part) => part.trim()))
      .filter((pair) => pair.length === 2 && pair[0] && pair[1])
      .map(([shorthand, full]) => [shorthand.toLowerCase(), full])
  );

  // Only a whole term, never a fragment. Matching inside what is being typed
  // would rewrite "el" on the way to "elizabeth arden".
  const expand = (term) => aliases.get(term.trim().toLowerCase()) || term;

  const money = (cents) =>
    new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100);

  const escape = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // product_type carries the brand on this store, but sometimes a department.
  const GENERIC = new Set(['beauty', 'department', 'medicine', 'medicines', 'natural health', 'fragrance', 'gifts', 'gift', 'sale', 'clearance', 'general', 'other', 'life pharmacy st lukes']);

  const brandOf = (product) => {
    const value = (product.product_type || '').trim();
    return GENERIC.has(value.toLowerCase()) ? '' : value;
  };

  // Gift-with-purchase campaigns, read from the same config the cart uses so
  // the dropdown cannot promise a gift the cart will not honour.
  // Read on first use rather than at load. The config is emitted at the end of
  // the body and this script is loaded from the header, so reading it eagerly
  // would depend on defer ordering that a later change could quietly break.
  let giftConfig = null;

  const gwp = () => {
    if (giftConfig === null) {
      try {
        const node = document.querySelector('[data-gwp-config]');
        giftConfig = node ? JSON.parse(node.textContent) : {};
      } catch (e) {
        giftConfig = {};
      }
    }
    return giftConfig;
  };

  const lowerTags = (tags) => (Array.isArray(tags) ? tags.map((t) => String(t).toLowerCase().trim()) : []);

  // The badge a qualifying product earns.
  const giftFor = (tags) => {
    const campaigns = gwp().campaigns || [];
    if (!campaigns.length) return '';
    const lower = lowerTags(tags);
    const hit = campaigns.find((campaign) => lower.includes(campaign.tag));
    return hit ? hit.badge : '';
  };

  // The gift itself, which is a different thing entirely. Showing "$0.00"
  // beside it reads as a free product anyone can take, which is exactly the
  // impression the rest of this feature exists to remove.
  // Returns the label to show on the gift itself, or '' if this is an ordinary
  // product. The wording comes from the campaign's badge field so it is the
  // same phrase the shop uses everywhere else, and changing it in admin
  // changes it here too.
  const giftOnlyLabel = (product) => {
    const config = gwp();
    const owning = (config.campaigns || []).find(
      (campaign) => Number(campaign.giftProductId) === Number(product.id)
    );
    if (owning) return owning.badge || 'Gift with purchase';

    const tag = (config.giftOnlyTag || '').toLowerCase();
    if (tag && lowerTags(product.tags).includes(tag)) return 'Gift with purchase';
    return '';
  };

  // Best seller and trending, read from the tags suggest.json already returns.
  // Same trick as the gift badge: no second request, no list of ids shipped to
  // every page.
  // Returns the kind as well as the words. The colour lives on the --best and
  // --trending modifiers, so a badge rendered without one is just floating text.
  // Same order as stlukes-selling-badge.liquid, and it has to stay that way: a
  // product badged "Trending" on the collection page and "New in" in the search
  // dropdown reads as two different products.
  const sellingFor = (tags) => {
    const lower = lowerTags(tags);
    if (lower.includes('trending')) return { kind: 'trending', label: 'Trending' };
    if (lower.includes('best-seller')) return { kind: 'best', label: 'Best seller' };
    // "New In" is Toniq's tag, written on the way in from the POS.
    if (lower.includes('new in')) return { kind: 'new', label: 'New in' };
    return null;
  };

  const stripBrand = (title, brand) => {
    if (!brand) return title;
    const lower = title.toLowerCase();
    return lower.startsWith(brand.toLowerCase()) ? title.slice(brand.length).trim() : title;
  };

  let controller = null;
  let debounce = null;
  let active = -1;

  const setOpen = (open) => {
    panel.hidden = !open;
    if (scrim) scrim.hidden = !open;
    root.classList.toggle('ssearch--open', open);
    input.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('ssearch-locked', open && window.matchMedia('(max-width: 1023px)').matches);
  };

  const openOverlay = () => {
    root.classList.add('ssearch--overlay');
    setOpen(true);
    input.focus();
  };

  const close = () => {
    setOpen(false);
    root.classList.remove('ssearch--overlay');
    active = -1;
  };

  // Shopify's suggest endpoint returns fuzzy matches alongside real ones: "estee"
  // brings back "ester c" and every vitamin containing it. Those are noise in a
  // completion list, so only suggestions that actually contain what was typed
  // survive, and the ones that start with it come first.
  const rankQueries = (queries, term) => {
    const needle = term.toLowerCase();

    return queries
      .map((entry) => (typeof entry === 'string' ? { text: entry } : entry))
      .map((entry) => ({ text: (entry.text || '').trim() }))
      .filter((entry) => {
        const text = entry.text.toLowerCase();
        return text.includes(needle) && text !== needle;
      })
      .sort((a, b) => {
        const starts = (value) => (value.text.toLowerCase().startsWith(needle) ? 0 : 1);
        return starts(a) - starts(b) || a.text.length - b.text.length;
      })
      .slice(0, 5);
  };

  // The API also sends styled_text with its own markup. Building the emphasis
  // here instead keeps anything the endpoint returns out of the DOM as HTML.
  const emphasise = (text, term) => {
    const at = text.toLowerCase().indexOf(term.toLowerCase());
    if (at < 0) return `<strong>${escape(text)}</strong>`;

    const before = text.slice(0, at);
    const match = text.slice(at, at + term.length);
    const after = text.slice(at + term.length);

    return [
      before ? `<strong>${escape(before)}</strong>` : '',
      `<span class="sq__typed">${escape(match)}</span>`,
      after ? `<strong>${escape(after)}</strong>` : ''
    ].join('');
  };

  const searchUrl = (term) =>
    `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(term)}&type=product`;

  const render = (data, term) => {
    const products = data?.resources?.results?.products ?? [];
    const collections = data?.resources?.results?.collections ?? [];
    const pages = data?.resources?.results?.pages ?? [];
    const queries = rankQueries(data?.resources?.results?.queries ?? [], term);

    const queryRows = queries
      .map(
        (entry) => `
          <a class="sq" href="${escape(searchUrl(entry.text))}" role="option" data-suggest="${escape(entry.text)}">
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10.4 10.4 14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span class="sq__text">${emphasise(entry.text, term)}</span>
          </a>`
      )
      .join('');

    if (!products.length && !collections.length && !pages.length) {
      // A spelling that matches nothing can still have a completion that does,
      // which is more use than telling someone to try again.
      results.innerHTML = `
        <p class="ssearch__empty">
          Nothing matched <strong>${escape(term)}</strong>.
          ${queryRows ? 'Did you mean one of these?' : 'Try a brand, a symptom, or a product name.'}
        </p>
        ${queryRows}`;
      return;
    }

    const productRows = products
      .map((product) => {
        const brand = brandOf(product);
        const title = stripBrand(product.title, brand);
        const image = product.featured_image?.url
          ? `${product.featured_image.url}${product.featured_image.url.includes('?') ? '&' : '?'}width=120`
          : '';
        const cents = (value) =>
          typeof value === 'string' ? Math.round(parseFloat(value.replace(/[^0-9.]/g, '')) * 100) : value;

        const price = cents(product.price);
        // The suggestion payload has no compare_at_price, only the min and max
        // across the variants. The min is the one to read: with a range, the
        // saving being advertised is the one on the cheapest variant, which is
        // the price shown beside it.
        const was = cents(product.compare_at_price_min);
        const onSale = was > 0 && price > 0 && was > price;
        const saved = onSale ? Math.round(((was - price) / was) * 100) : 0;

        // suggest.json returns the product's tags, so a gift-with-purchase
        // badge costs nothing extra here. Without them the alternative was
        // shipping every qualifying handle to every page, which for the Estée
        // Lauder campaign alone is 1,388 of them.
        const gift = giftFor(product.tags);
        const giftLabel = giftOnlyLabel(product);
        const giftOnly = giftLabel !== '';
        // One badge per row. A gift offer beats a sales rank, because it is
        // something the shopper can act on rather than something to know.
        const selling = sellingFor(product.tags);

        return `
          <a class="sres${onSale && !giftOnly ? ' sres--sale' : ''}" href="${escape(product.url)}" role="option">
            <span class="sres__media">${image ? `<img src="${escape(image)}" alt="" width="46" height="46" loading="lazy">` : ''}</span>
            <span class="sres__body">
              ${brand ? `<span class="sres__brand">${escape(brand)}</span>` : ''}
              <span class="sres__title">${escape(title)}</span>
              ${giftOnly
                ? `<span class="sres__note">${escape(giftLabel)}</span>`
                : gift
                  ? `<span class="gwp-badge gwp-badge--suggest">${escape(gift)}</span>`
                  : selling
                    ? `<span class="sell-badge sell-badge--${selling.kind} sell-badge--suggest">${escape(selling.label)}</span>`
                    : ''}
            </span>
            <span class="sres__price${giftOnly ? ' sres__price--gift' : ''}">
              ${giftOnly ? 'Free' : escape(money(price))}
              ${onSale && !giftOnly ? `<s class="sres__was">${escape(money(was))}</s>` : ''}
            </span>
            ${onSale && saved > 0 && !giftOnly ? `<span class="sres__save">&minus;${saved}%</span>` : ''}
          </a>`;
      })
      .join('');

    const linkRows = [...collections, ...pages]
      .slice(0, 4)
      .map(
        (item) => `
          <a class="sres sres--link" href="${escape(item.url)}" role="option">
            <span class="sres__body"><span class="sres__title">${escape(item.title)}</span></span>
            <span class="sres__kind">${item.url.includes('/collections/') ? 'Collection' : 'Page'}</span>
          </a>`
      )
      .join('');

    // Suggestions first: someone three letters into a brand name wants to finish
    // the word, not pick from six products they have not narrowed down yet.
    results.innerHTML = `
      ${queryRows ? `<p class="ssearch__group">Suggestions</p>${queryRows}` : ''}
      ${productRows ? `<p class="ssearch__group">Products</p>${productRows}` : ''}
      ${linkRows ? `<p class="ssearch__group">Departments and pages</p>${linkRows}` : ''}
      <a class="ssearch__all" href="${escape(searchUrl(term))}">
        See all results for “${escape(term)}”
      </a>`;
  };

  const search = async (typed) => {
    controller?.abort();
    controller = new AbortController();

    // Everything downstream works from the expanded term: the suggestions, the
    // "see all results" link, and the note saying what was actually searched.
    const term = expand(typed);
    const substituted = term !== typed;

    const root_url = window.Shopify?.routes?.root || '/';
    const q = encodeURIComponent(term);
    const suggest = (types, limit) =>
      `${root_url}search/suggest.json?q=${q}&resources[type]=${types}&resources[limit]=${limit}&resources[options][unavailable_products]=last`;

    // Two requests, not one. Asking for query alongside product splits the limit
    // across the resource types: "estee" came back with six completions on its
    // own and two combined, both of which were then filtered out as noise. They
    // go in parallel, so this costs nothing in time.
    try {
      const [main, queried] = await Promise.all([
        fetch(suggest('product,collection,page', 6), { signal: controller.signal }).then((r) => r.json()),
        fetch(suggest('query', 10), { signal: controller.signal })
          .then((r) => r.json())
          .catch(() => null)
      ]);

      const data = main;
      const queries = queried?.resources?.results?.queries;
      if (queries && data?.resources?.results) data.resources.results.queries = queries;

      render(data, term);

      // Said out loud. A shopper who typed ysl and is shown Yves Saint Laurent
      // should be told, both so the results make sense and so they can see the
      // shop understood them.
      if (substituted && results.firstChild) {
        results.insertAdjacentHTML(
          'afterbegin',
          `<p class="ssearch__alias">Showing results for <strong>${escape(term)}</strong></p>`
        );
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      results.innerHTML = '<p class="ssearch__empty">Search is unavailable right now. Press enter to search the full store.</p>';
    }
  };

  input.addEventListener('input', () => {
    const term = input.value.trim();
    clearButton.hidden = term.length === 0;
    active = -1;

    clearTimeout(debounce);

    if (term.length < 2) {
      results.innerHTML = '';
      showIdle(true);
      setOpen(true);
      return;
    }

    showIdle(false);
    setOpen(true);
    debounce = setTimeout(() => search(term), 180);
  });

  input.addEventListener('focus', () => {
    const term = input.value.trim();
    showIdle(term.length < 2);
    // On the results page the field arrives filled in. Without this, focusing
    // it opened an empty panel: no idle block, and no results fetched yet.
    if (term.length >= 2 && !results.innerHTML.trim()) search(term);
    setOpen(true);
  });

  // Arrow keys move through suggestions; enter on a highlighted row follows it.
  input.addEventListener('keydown', (event) => {
    const options = [...results.querySelectorAll('[role="option"], .ssearch__all')];
    if (!options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      active = event.key === 'ArrowDown'
        ? Math.min(active + 1, options.length - 1)
        : Math.max(active - 1, -1);
      options.forEach((option, index) => option.classList.toggle('sres--active', index === active));
      if (active >= 0) options[active].scrollIntoView({ block: 'nearest' });
      return;
    }

    if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      options[active].click();
    }
  });

  clearButton.addEventListener('click', () => {
    input.value = '';
    clearButton.hidden = true;
    results.innerHTML = '';
    showIdle(true);
    input.focus();
  });

  // A suggestion refines the query in place. Someone completing "estee" to
  // "estee lauder" is still narrowing down, and sending them to a results page
  // for every keystroke of that is a page load they did not ask for. Modified
  // clicks and middle clicks fall through to the link so a new tab still works.
  results.addEventListener('click', (event) => {
    const suggestion = event.target.closest('[data-suggest]');
    if (!suggestion) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    input.value = suggestion.dataset.suggest;
    clearButton.hidden = false;
    active = -1;
    input.focus();
    search(input.value);
  });

  openers.forEach((opener) => opener.addEventListener('click', (event) => {
    event.preventDefault();
    openOverlay();
  }));

  document.querySelectorAll('[data-search-close]').forEach((button) =>
    button.addEventListener('click', close)
  );

  document.addEventListener('click', (event) => {
    if (root.contains(event.target) || event.target.closest('[data-search-open]')) return;
    close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      close();
      input.blur();
    }
  });

  // Pressing enter has to expand the shorthand too, or the dropdown finds Jean
  // Paul Gaultier and the results page it hands you to finds nothing.
  const form = root.querySelector('form');
  if (form) {
    form.addEventListener('submit', () => {
      const expanded = expand(input.value);
      if (expanded !== input.value) input.value = expanded;
    });
  }

  // And for anyone arriving at /search?q=jpg directly, from a bookmark or a
  // link, since they never touched the field. Replaces rather than pushes, so
  // the back button does not land them on the query that found nothing. The
  // expansion is never itself a shorthand, so this cannot loop.
  if (window.location.pathname.endsWith('/search')) {
    const params = new URLSearchParams(window.location.search);
    const typed = params.get('q') || '';
    const expanded = expand(typed);

    if (expanded !== typed) {
      params.set('q', expanded);
      window.location.replace(`${window.location.pathname}?${params}`);
    }
  }
})();
